import { createSignal } from "solid-js";

/**
 * ACTIVE organization (company / supermarket) per user — shared state
 * at MODULE level so ALL instances of `useActiveOrg` (one per
 * WMS route + the header switcher) react to the same change, and
 * persisted in localStorage to survive reloads.
 *
 * The key is a `userId → scopeId` map (not a flat value) because the app is
 * multi-account: each account remembers ITS active organization, and switching
 * accounts does not inherit the pointer from another (same spirit as
 * comandero and the workspace-switcher in time-tracker).
 *
 * `scopeId` can be:
 *   - the id of a real organization (company where user is a member), or
 *   - the `userId` itself → PERSONAL workspace (inventory `organization_id =
 *     user_id`, F1; also visitor mode with `VISITOR_ID`).
 *
 * The pointer is only a PREFERENCE: `useActiveOrg` validates it against
 * orgs actually synced locally and falls back to "Personal" (the userId) if it
 * no longer exists (deleted org / stale data / not yet synced) — never blocks
 * (self-healing).
 */
const STORAGE_KEY = "activeOrgByUser";

type ActiveMap = Readonly<Record<string, string>>;

function readPersisted(): ActiveMap {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      );
      return Object.fromEntries(entries);
    }
  } catch {
    // localStorage can fail (private mode) or the JSON can be corrupt; the
    // in-memory state is enough and the fallback is "Personal".
  }
  return {};
}

const [activeMap, setActiveMap] = createSignal<ActiveMap>(readPersisted());

/**
 * Epoch of an EXPLICIT organization change. It increments ONLY when the
 * user intentionally changes organizations (`setActiveScopeIdFor`), never during
 * scope settling at boot (guest→real user, late sync…).
 *
 * The isolation guard (`useResetOnOrgSwitch`) watches this epoch to
 * discard open entity pointers (`?picking=`, `?po=`, `?count=`…) when
 * changing organizations, WITHOUT the risk of deleting a legitimate deep link
 * during startup (which would happen if watching the scope directly).
 */
const [switchEpoch, setSwitchEpoch] = createSignal(0);

/** Active org pointer of the user (may point to a no longer existent org). */
export function activeScopeIdFor(userId: string): string | null {
  return activeMap()[userId] ?? null;
}

/** Sets (and persists) the user's active organization and signals an explicit change. */
export function setActiveScopeIdFor(userId: string, scopeId: string): void {
  const current = activeMap()[userId] ?? null;
  const next = { ...activeMap(), [userId]: scopeId };
  setActiveMap(next);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Best-effort persistence; the in-memory signal has already changed.
  }
  // It only counts as a "switch" if the destination really changes — so re-setting
  // the same scope (e.g. reactivating the already active one) does not trigger the guard.
  if (current !== scopeId) {
    setSwitchEpoch((n) => n + 1);
  }
}

/** Reactive accessor for the explicit organization-change epoch (for the guard). */
export function orgSwitchEpoch(): number {
  return switchEpoch();
}

/** Tests only: reset the in-memory state by re-reading localStorage. */
export function resetActiveOrgStateForTests(): void {
  setActiveMap(readPersisted());
}
